<template>
  <v-form ref="form" @submit.prevent>
    <FormSection>
      <ComplaintFormTabs
        v-model="formData.modelType"
        :items="modelTypeItems"
        :required="true"
        :error-messages="modelTypeErrors"
        @tab-click="onModelTypeClick"
      />
    </FormSection>

    <FormSection element-type="div" :disabled="!formData.modelType" :hidden-disabled-mobile="true">
      <FormSection>
        <BaseTextfield
          v-model="formData.engineSerialNo"
          class="mb-6"
          :error-messages="engineSerialNoErrors"
          :hide-details="!engineSerialNoErrors.length"
          :label="$t('complaintForm.ticket.engine.engine_series_no')"
          counter
          required
          v-on="getTouchListeners('formData.engineSerialNo')"
        />

        <BaseAlert v-if="formData.modelType === MODEL_TYPE_IMPULSE_EVO" icon="info">
          <template #message>
            <BaseRichtext :class="$style.pre">
              {{ $t('complaintForm.ticket.engine.worktime_tooltip').trim() }}
            </BaseRichtext>
          </template>
        </BaseAlert>

        <BaseAlert type="warning" icon="info" :message="modelTypeInfo.message">
          <v-img
            v-if="modelTypeInfo.image"
            :min-height="190"
            :max-height="190"
            :src="modelTypeInfo.image.imageUrl"
            :lazy-src="modelTypeInfo.image.imageUrl"
            :alt="modelTypeInfo.image.name"
            cover
            position="top center"
          />
        </BaseAlert>
      </FormSection>

      <FormSection>
        <BaseTextfield
          v-model="formData.mileage"
          class="mb-6"
          :error-messages="mileageErrors"
          :hide-details="!mileageErrors.length"
          :label="$t('complaintForm.ticket.engine.mileage_in_km')"
          required
          v-on="getTouchListeners('formData.mileage')"
        />
      </FormSection>
    </FormSection>

    <ComplaintFormStepperControls
      :is-valid="isValid"
      :has-required-fields="true"
      :prev-step="prevStep"
      :next-step="submit"
    />
  </v-form>
</template>

<script>
import { get } from 'lodash-es'
import { mapMutations, mapGetters } from 'vuex'
import { mask } from 'vue-the-mask'
import { required, numeric, between } from 'vuelidate/lib/validators'

import { decimal } from '@/js/components/Forms/helper/form-validation-helper.js'

import ComplaintFormImageDataMixin from '@/js/components/Forms/ComplaintForm/_mixins/complaint-form-image-data-mixin.js'

import StepperMixin from '@/js/mixins/stepper-mixin.js'
import FormValidationMixin from '@/js/components/Forms/mixins/form-validation-mixin.js'
import ComplaintFormMixin from '@/js/components/Forms/ComplaintForm/_mixins/complaint-form-default-mixin.js'

import FORM_CONSTANTS from '@/js/components/Forms/ComplaintForm/_store-module/constants.js'
import FORM_MUTATION from '@/js/components/Forms/ComplaintForm/_store-module/mutation-types.js'
import FORM_GETTER from '@/js/components/Forms/ComplaintForm/_store-module/getter-types.js'

import FormSection from '@/js/components/Forms/FormSection.vue'

import ComplaintFormTabs from '../../ComplaintFormTabs.vue'
import ComplaintFormStepperControls from '../../ComplaintFormStepperControls.vue'

export const COMPLAINT_FORM_IMPULSE_PRODUCT_DATA_CONSTANTS = {
  MODEL_TYPE_IMPULSE_2_0: 'Impulse 2.0',
  MODEL_TYPE_IMPULSE_EVO: 'Impulse EVO',
  MODEL_TYPE_CONTINENTAL: 'Continental',
}

export default {
  name: 'ComplaintFormWorktimeProductData',
  components: {
    FormSection,
    ComplaintFormTabs,
    ComplaintFormStepperControls,
  },
  directives: {
    mask,
  },
  mixins: [FormValidationMixin, ComplaintFormMixin, ComplaintFormImageDataMixin],
  props: {
    ...StepperMixin.props,
  },
  data() {
    return {
      // Constants
      ...COMPLAINT_FORM_IMPULSE_PRODUCT_DATA_CONSTANTS,
      formData: {
        modelType: null,
        engineSerialNo: null,
        mileage: null,
        calculationDesire:
          FORM_CONSTANTS.CALCULATION_DESIRE_TYPES.CALCULATION_DESIRE_WORKING_TIME_COMPENSATION,
      },
      // Other data
      prevModelType: null,
    }
  },
  validations: {
    formData: {
      modelType: {
        required,
      },
      engineSerialNo: {
        required,
        numeric,
        between: between(732845, 828873),
      },
      mileage: {
        required,
        decimal,
      },
    },
  },
  computed: {
    ...mapGetters(FORM_CONSTANTS.STORE_ID, {
      language: FORM_GETTER.LANGUAGE,
    }),

    /** Computed helper */
    modelTypeInfo() {
      switch (this.formData.modelType) {
        case COMPLAINT_FORM_IMPULSE_PRODUCT_DATA_CONSTANTS.MODEL_TYPE_IMPULSE_2_0: {
          return {
            message: this.$t('complaintForm.ticket.engine.engine_series_no_find2'),
            image: get(this.imageData, 'u38', {}),
          }
        }
        case COMPLAINT_FORM_IMPULSE_PRODUCT_DATA_CONSTANTS.MODEL_TYPE_IMPULSE_EVO: {
          return {
            message: this.$t('complaintForm.ticket.engine.engine_series_nr_find_vo'),
            image: get(this.imageData, 'u71', {}),
          }
        }

        default: {
          return {}
        }
      }
    },

    /** Select items */
    modelTypeItems() {
      return [
        {
          text: COMPLAINT_FORM_IMPULSE_PRODUCT_DATA_CONSTANTS.MODEL_TYPE_IMPULSE_2_0,
          value: COMPLAINT_FORM_IMPULSE_PRODUCT_DATA_CONSTANTS.MODEL_TYPE_IMPULSE_2_0,
          image: get(this.imageData, 'u145'),
        },
        {
          text: COMPLAINT_FORM_IMPULSE_PRODUCT_DATA_CONSTANTS.MODEL_TYPE_IMPULSE_EVO,
          value: COMPLAINT_FORM_IMPULSE_PRODUCT_DATA_CONSTANTS.MODEL_TYPE_IMPULSE_EVO,
          image: get(this.imageData, 'u151'),
        },
      ]
    },

    /** Errors */
    modelTypeErrors() {
      return this.formErrorHelper('formData.modelType', [this.chooseError])
    },
    engineSerialNoErrors() {
      return this.formErrorHelper('formData.engineSerialNo', [
        this.requiredError,
        this.numericError,
        {
          condition: 'between',
          error: this.workTimeErrorMessage,
        },
      ])
    },
    mileageErrors() {
      return this.formErrorHelper('formData.mileage', [
        this.requiredError,
        {
          condition: 'decimal',
          error: this.numbersErrorMessage,
        },
      ])
    },
  },
  watch: {
    modelTypeItems: {
      handler: function (newValue, oldValue) {
        if (newValue !== oldValue) {
          this.formData.modelType = get(this.modelTypeItems, '0.value', null)
        }
      },
    },
  },
  methods: {
    ...mapMutations(FORM_CONSTANTS.STORE_ID, {
      updateFormData: FORM_MUTATION.UPDATE_FORM_DATA,
    }),
    onModelTypeClick(val) {
      if (val === this.prevModelType) {
        this.formData.modelType = null
      }

      this.prevModelType = this.formData.modelType
    },
    submitCallback() {
      this.updateFormData(this.formData)
      this.nextStep()
    },
  },
}
</script>

<style lang="scss" module>
.pre {
  white-space: pre-line;
}
</style>
